// BMSIT Auditorium & Events — complete API client
// Every action the Cloudflare Worker accepts is wrapped here.
const API_URL = process.env.REACT_APP_API_URL || "https://bmsit-auditorium-api.info-fortifyhub.workers.dev";
const TOKEN_KEY = "bmsit.token";
const USER_KEY  = "bmsit.user";

export const getToken      = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken      = t  => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
export const getStoredUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; } };
export const setStoredUser = u  => u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY);

export class ApiError extends Error {
  constructor(message, status, payload) { super(message); this.status = status; this.payload = payload; }
}

async function get(params = {}) {
  const url = new URL(API_URL);
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== "") url.searchParams.set(k, String(v)); });
  // Worker reads token from query string (?token=), NOT from Authorization header.
  // Authorization header also trips a CORS preflight the Worker rejects.
  const token = getToken();
  if (token) url.searchParams.set("token", token);
  const res = await fetch(url.toString());
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data?.message || "Request failed", res.status, data);
  return data;
}

async function post(body = {}) {
  const token = getToken();
  // Token travels in the JSON body (Worker reads body.token).
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, token: token || undefined }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data?.message || "Request failed", res.status, data);
  if (!data.success && data.authError) {
    setToken(null); setStoredUser(null);
    throw new ApiError("Session expired. Please sign in again.", 401, data);
  }
  if (!data.success && data.message) throw new ApiError(data.message, res.status, data);
  return data;
}

const api = {
  // ── Auth ─────────────────────────────────────────────────────────
  requestOtp:        (email)              => post({ action: "requestOtp", email }),
  verifyOtp:         (email, code)        => post({ action: "verifyOtp", email, code }),
  whoami:            ()                   => get({ action: "whoami" }),
  photographerLogin: (email, code, subAction) => post({ action: "photographerLogin", subAction, email, code }),

  // ── Halls ─────────────────────────────────────────────────────────
  listHalls:         ()                   => get({ action: "listHalls" }),
  checkAvail:        (hall, date, ts)     => get({ action: "checkAvailability", hall, date, timeSlot: ts }),
  checkAvailRange:   (hall, s, e, ts)     => get({ action: "checkAvailabilityRange", hall, startDate: s, endDate: e, timeSlot: ts }),

  // ── Bookings ──────────────────────────────────────────────────────
  myBookings:        ()                   => get({ action: "listMyBookings" }),
  trackBooking:      (bookingID)          => get({ action: "trackBookingStatus", bookingID }),
  addBooking:        (b)                  => post({ action: "addBooking", ...b }),
  cancelBooking:     (bookingID)          => post({ action: "cancelBooking", bookingID }),
  editBooking:       (bookingID, updates) => post({ action: "editBooking", bookingID, updates }),
  uploadLetter:      (bookingID, fileName, mimeType, data) =>
                                            post({ action: "uploadPermissionLetter", bookingID, fileName, mimeType, data }),

  // ── Admin bookings ────────────────────────────────────────────────
  adminListBookings: async (opts = {}) => {
    const r = await get({ action: "adminListBookings", ...opts });
    const rows = Array.isArray(r?.bookings) ? r.bookings : [];
    // Worker returns raw snake_case DB rows; map to the camelCase the admin UI
    // expects. Without this bookingID is undefined and status updates fail with
    // "Booking not found".
    const bookings = rows.map((b) => ({
      ...b,
      bookingID:        b.bookingID        ?? b.booking_id,
      eventName:        b.eventName        ?? b.event_name,
      auditorium:       b.auditorium       ?? b.hall_name,
      hallId:           b.hallId           ?? b.hall_id,
      deptClub:         b.deptClub         ?? b.dept_club,
      timeSlot:         b.timeSlot         ?? b.time_slot,
      contactInfo:      b.contactInfo      ?? b.contact_info,
      specialReqs:      b.specialReqs      ?? b.special_reqs,
      permissionLetter: b.permissionLetter ?? b.permission_letter,
      adminNote:        b.adminNote        ?? b.admin_note,
      seriesTotal:      b.seriesTotal      ?? b.series_total,
    }));
    return { ...r, bookings };
  },
  adminUpdateStatus: (bookingID, newStatus, note) =>
                                            post({ action: "adminUpdateStatus", bookingID, newStatus, note }),
  adminEditBooking:  (bookingID, updates) => post({ action: "adminEditBooking", bookingID, updates }),

  // ── Calendar ──────────────────────────────────────────────────────
  calendarData:      (startDate, endDate) => get({ action: "getCalendarData", startDate, endDate }),
  eventsByDate:      (date)               => get({ action: "getEventsByDate", date }),

  // ── Events ────────────────────────────────────────────────────────
  publicFeed:        (opts = {})          => get({ action: "publicEventsFeed", ...opts }),
  recentFeed:        ()                   => get({ action: "recentEventsFeed" }),
  getEventProfile:   (id, qrToken)        => get({ action: "getEventProfile", bookingID: id, qrToken: qrToken || "" }),
  saveEventProfile:  (bookingID, fields)  => post({ action: "saveEventProfile", bookingID, fields }),
  getStandaloneEvent:(eventID)            => get({ action: "getStandaloneEvent", eventID }),
  saveStandaloneEvent:(eventID, fields)   => post({ action: "saveStandaloneEvent", eventID, fields }),
  myStandaloneEvents:()                   => get({ action: "listMyStandaloneEvents" }),
  deleteStandaloneEvent:(eventID)         => post({ action: "deleteStandaloneEvent", eventID }),
  myActivity:        ()                   => get({ action: "getMyActivity" }),

  // ── Registrations ─────────────────────────────────────────────────
  register:          (bookingID, formData) => post({ action: "registerParticipant", bookingID, formData }),
  cancelReg:         (regID, email)        => post({ action: "cancelRegistration", regID, email }),
  listRegs:          (bookingID)           => get({ action: "listRegistrations", bookingID }),
  // exportRegistrations is POST-only in the worker (reads s('bookingID') from body)
  exportRegs:        (bookingID)           => post({ action: "exportRegistrations", bookingID }),
  getMyRegistration: (regID, email)        => get({ action: "getMyRegistration", regID, email }),
  // adminApproveReg: worker reads s('action') for approve/reject AND routes on body.action
  // Send as regAction field; worker updated to read s('regAction')||s('action')
  adminApproveReg:   (regID, regAction)    => post({ action: "adminApproveReg", regID, regAction }),
  adminRemoveReg:    (regID)               => post({ action: "adminRemoveReg", regID }),
  bulkApprove:       (bookingID, regIDs, bulkAction) =>
                                            post({ action: "bulkApproveRegs", bookingID, regIDs, regAction: bulkAction }),
  broadcast:         (bookingID, subject, message) =>
                                            post({ action: "broadcastToRegistrants", bookingID, subject, message }),

  // ── Event team access (owner + coordinators) ──────────────────────
  // The event's creator is always the owner. The owner can grant other
  // staff ("coordinators") delegated access to registrations, check-in
  // and feedback for that specific event. Enforcement lives in the Worker.
  listEventTeam:     (bookingID)           => get({ action: "listEventTeam", bookingID }),
  addCoordinator:    (bookingID, email, perms) =>
                                            post({ action: "addEventCoordinator", bookingID, email, perms }),
  removeCoordinator: (bookingID, email)    => post({ action: "removeEventCoordinator", bookingID, email }),
  updateCoordinator: (bookingID, email, perms) =>
                                            post({ action: "updateEventCoordinator", bookingID, email, perms }),

  // ── Check-in ──────────────────────────────────────────────────────
  checkin:           (bookingID, query, gate, dayIndex) =>
                                            post({ action: "checkinManual", bookingID, query, gate, dayIndex }),
  addWalkIn:         (bookingID, name, email, usn, gate, dayIndex) =>
                                            post({ action: "addWalkIn", bookingID, name, email, usn, gate, dayIndex }),
  attendance:        (bookingID)           => get({ action: "getAttendanceSummary", bookingID }),

  // ── Post-event ────────────────────────────────────────────────────
  getPostEvent:      (bookingID)           => get({ action: "getPostEvent", bookingID }),
  savePostEvent:     (bookingID, fields)   => post({ action: "savePostEvent", bookingID, fields }),
  // getEventReport is POST-only in the worker (reads s('bookingID') from body)
  getReport:         (bookingID)           => post({ action: "getEventReport", bookingID }),
  issueCerts:        (bookingID, minDays)  => post({ action: "issueCertificates", bookingID, minDays }),
  revokeCert:        (certID)              => post({ action: "revokeCertificate", certID }),
  verifyCert:        (token)               => get({ action: "verifyCertificate", token }),
  closeEvent:        (bookingID)           => post({ action: "closeEvent", bookingID }),

  // ── Feedback ──────────────────────────────────────────────────────
  listFeedback:      (bookingID)           => get({ action: "listFeedback", bookingID }),
  submitFeedback:    (regID, email, ratings, comments, isAnon) =>
                                            post({ action: "submitFeedback", regID, email, ratings, comments, isAnon }),

  // ── Gallery ───────────────────────────────────────────────────────
  myGalleryEvents:   ()                    => get({ action: "listMyGalleryEvents" }),
  pendingGalleries:  ()                    => get({ action: "listPendingGalleries" }),
  galleryAll:        (bookingID)           => get({ action: "getEventGalleryAll", bookingID }),
  gallery:           (bookingID)           => get({ action: "getEventGallery", bookingID }),
  uploadGalleryPhoto:(body)                => post({ action: "uploadGalleryPhoto", ...body }),
  submitGalleryPhotos:(bookingID, photos)  => post({ action: "submitGalleryPhotos", bookingID, photos }),
  reviewPhoto:       (photoID, reviewAction, reason) =>
                                            post({ action: "reviewGalleryPhoto", photoID, reviewAction, reason }),
  publishGallery:    (bookingID)           => post({ action: "publishGallery", bookingID }),

  // ── Super Admin ───────────────────────────────────────────────────
  saListHalls:       ()                    => get({ action: "saListHalls" }),
  saSaveHall:        (hall, isNew)         => post({ action: "saSaveHall", hall, isNew }),
  saSetHallStatus:   (hallId, status, maintenanceNote) =>
                                            post({ action: "saSetHallStatus", hallId, status, maintenanceNote }),
  listPhotographers: ()                    => get({ action: "listPhotographers" }),
  addPhotographer:   (email, name)         => post({ action: "addPhotographer", email, name }),
  removePhotographer:(email)               => post({ action: "removePhotographer", email }),
};

export default api;
